import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp, Download, AlertTriangle, CheckCircle, Info } from 'lucide-react';

/**
 * DiagnosticsPanel
 *
 * Expandable section in TVDetails showing the full HDMI pipeline table,
 * system info, and actionable warnings. Admin-focused — shows everything
 * the TV app reports so technical staff can diagnose display issues
 * without ADB access.
 *
 * When diagnostics is null (TV app hasn't sent data yet), renders a
 * collapsed placeholder with a "not available" message.
 */

// ── Warning rules ──────────────────────────────────────────────────────
// Each rule tests the diagnostics object and returns a warning object
// or null. Warnings have { severity: 'error'|'warn'|'info', message, fix }.

const WARNING_RULES = [
  (d) => {
    const range = d?.hdmi?.colorRange;
    if (range === 'Limited' || range === 'limited') {
      return {
        severity: 'warn',
        message: 'HDMI color range is Limited (16-235)',
        fix: 'Content authored in Full range will appear washed out. Fix: adjust LED controller input settings to Full range, or configure the media player kernel to output Full range (requires custom firmware on RK3576).',
      };
    }
    return null;
  },
  (d) => {
    const bus = d?.hdmi?.busFormat;
    if (bus && bus.startsWith('YUV')) {
      return {
        severity: 'info',
        message: `HDMI bus format is ${bus} (YUV color space)`,
        fix: 'Color-critical content may benefit from RGB output. Most RK3576 devices hardcode YUV in the kernel DRM driver — this is expected behavior, not a fault.',
      };
    }
    return null;
  },
  (d) => {
    const overscan = d?.android?.overscanPercent;
    if (overscan != null && overscan > 0) {
      return {
        severity: overscan > 3 ? 'error' : 'warn',
        message: `Overscan is ${overscan}% — content edges will be cropped`,
        fix: 'Fix: Settings > Display > Overscan on the media player, or `adb shell settings put global overscan 0,0,0,0`. If the crop persists, it may be the LED controller hardware — check panel mapping.',
      };
    }
    return null;
  },
  (d) => {
    const selinux = d?.android?.selinux;
    if (selinux === 'Enforcing') {
      return {
        severity: 'info',
        message: 'SELinux is Enforcing — some diagnostic reads may be blocked',
        fix: 'This is normal and secure. Some debugfs paths require root + permissive mode to read. Diagnostics will show null for those fields.',
      };
    }
    return null;
  },
  (d) => {
    if (d?.android?.rootAvailable === false) {
      return {
        severity: 'info',
        message: 'Device does not have root access',
        fix: 'Some HDMI pipeline diagnostics (bus format, color encoding) require root to read from debugfs. These fields will be unavailable.',
      };
    }
    return null;
  },
];

const SEVERITY_STYLES = {
  error: { bg: '#fee2e2', border: '#fecaca', fg: '#991b1b', icon: AlertTriangle, iconColor: '#dc2626' },
  warn: { bg: '#fef9c3', border: '#fde68a', fg: '#92400e', icon: AlertTriangle, iconColor: '#d97706' },
  info: { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af', icon: Info, iconColor: '#3b82f6' },
};

const DiagnosticsPanel = ({ diagnostics, device }) => {
  const [expanded, setExpanded] = useState(false);

  const warnings = diagnostics
    ? WARNING_RULES.map((rule) => rule(diagnostics)).filter(Boolean)
    : [];

  const handleDownload = useCallback(() => {
    const payload = {
      deviceId: device?.deviceId,
      tvId: device?.tvId,
      displayName: device?.displayName,
      edidFingerprint: device?.edidFingerprint,
      diagnostics,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostics-${device?.deviceId || 'unknown'}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [diagnostics, device]);

  const hdmi = diagnostics?.hdmi;
  const android = diagnostics?.android;
  const pipeline = diagnostics?.processingPipeline;

  return (
    <div style={{
      border: '1px solid #e2e8f0',
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 16,
    }}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: '#f8fafc',
          border: 'none',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 700,
          color: '#475569',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Diagnostics
          {warnings.length > 0 && (
            <span style={{
              background: warnings.some((w) => w.severity === 'error') ? '#fee2e2' : '#fef9c3',
              color: warnings.some((w) => w.severity === 'error') ? '#dc2626' : '#a16207',
              padding: '1px 6px',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 700,
            }}>
              {warnings.length}
            </span>
          )}
        </span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '12px 16px', background: '#ffffff' }}>
          {!diagnostics ? (
            <div style={{
              fontSize: 13,
              color: '#94a3b8',
              textAlign: 'center',
              padding: '16px 0',
              fontStyle: 'italic',
            }}>
              No diagnostics data received yet. The TV app needs to be updated
              to send display pipeline information in its heartbeat.
            </div>
          ) : (
            <>
              {/* Warnings */}
              {warnings.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  {warnings.map((w, i) => {
                    const style = SEVERITY_STYLES[w.severity];
                    const WarnIcon = style.icon;
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 8,
                          padding: '8px 10px',
                          background: style.bg,
                          border: `1px solid ${style.border}`,
                          borderRadius: 6,
                          marginBottom: 6,
                          alignItems: 'flex-start',
                        }}
                      >
                        <WarnIcon size={14} style={{ color: style.iconColor, flexShrink: 0, marginTop: 1 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: style.fg }}>
                            {w.message}
                          </div>
                          <div style={{ fontSize: 11, color: style.fg, opacity: 0.8, marginTop: 2 }}>
                            {w.fix}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* HDMI Pipeline */}
              {hdmi && (
                <div style={{ marginBottom: 14 }}>
                  <div style={tableLabelStyle}>HDMI Pipeline</div>
                  <table style={tableStyle}>
                    <tbody>
                      <Row label="Bus Format" value={hdmi.busFormat} />
                      <Row label="Color Encoding" value={hdmi.colorEncoding} />
                      <Row label="Color Range" value={hdmi.colorRange} highlight={hdmi.colorRange === 'Limited' || hdmi.colorRange === 'limited'} />
                      <Row label="Resolution" value={hdmi.resolution} />
                      <Row label="Panel EDID Name" value={hdmi.panelEdidName} />
                      <Row label="Manufacturer" value={hdmi.panelEdidManufacturer} />
                      <Row label="CEA Extension" value={hdmi.hasCeaExtension != null ? (hdmi.hasCeaExtension ? 'Yes' : 'No') : null} />
                    </tbody>
                  </table>
                </div>
              )}

              {/* Android System */}
              {android && (
                <div style={{ marginBottom: 14 }}>
                  <div style={tableLabelStyle}>Android System</div>
                  <table style={tableStyle}>
                    <tbody>
                      <Row label="Android Version" value={android.version} />
                      <Row label="SELinux" value={android.selinux} />
                      <Row label="Root Available" value={android.rootAvailable != null ? (android.rootAvailable ? 'Yes' : 'No') : null} />
                      <Row label="Overscan" value={android.overscanPercent != null ? `${android.overscanPercent}%` : null} highlight={android.overscanPercent > 0} />
                      <Row label="Display Density" value={android.displayDensity != null ? `${android.displayDensity} dpi` : null} />
                      <Row label="Framebuffer" value={android.framebufferSize} />
                    </tbody>
                  </table>
                </div>
              )}

              {/* Processing Pipeline */}
              {pipeline && (
                <div style={{ marginBottom: 14 }}>
                  <div style={tableLabelStyle}>Content Pipeline</div>
                  <table style={tableStyle}>
                    <tbody>
                      <Row label="Version" value={pipeline.version != null ? `v${pipeline.version}` : null} />
                    </tbody>
                  </table>
                </div>
              )}

              {/* Download button */}
              <button
                onClick={handleDownload}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#475569',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                <Download size={12} />
                Download diagnostic JSON
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Table primitives ───────────────────────────────────────────────────

const tableLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 6,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 12,
};

const Row = ({ label, value, highlight = false }) => {
  if (value == null) return null;
  return (
    <tr>
      <td style={{
        padding: '4px 0',
        color: '#64748b',
        width: '40%',
        verticalAlign: 'top',
      }}>
        {label}
      </td>
      <td style={{
        padding: '4px 0',
        color: highlight ? '#dc2626' : '#0f172a',
        fontWeight: highlight ? 600 : 400,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
      }}>
        {String(value)}
      </td>
    </tr>
  );
};

export default DiagnosticsPanel;
